import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy {
  onTimeout = new Subject<void>();
  onTimeoutWarning = new Subject<number>();

  private readonly storageKey = 'idle_service_activity';

  private idleSeconds = 0;
  private timeoutSeconds = 0;
  private interruptSources: string[] | null = null;

  private interruptSub?: Subscription;
  private storageSub?: Subscription;
  private countdownSub?: Subscription;
  private idleTimer?: ReturnType<typeof setTimeout>;

  setIdle(seconds: number): void {
    this.idleSeconds = seconds;
  }

  setTimeout(seconds: number): void {
    this.timeoutSeconds = seconds;
  }

  setInterrupts(sources: string[] | null): void {
    this.interruptSources = sources;
  }

  watch(): void {
    this.stop();
    this.startIdlePhase();

    if (this.interruptSources?.length) {
      this.interruptSub = merge(
        ...this.interruptSources.map(eventName => fromEvent(document, eventName))
      ).subscribe(() => {
        this.startIdlePhase();
        this.broadcastActivity();
      });

      this.storageSub = fromEvent<StorageEvent>(window, 'storage')
        .pipe(filter(event => event.key === this.storageKey))
        .subscribe(() => {
          this.startIdlePhase();
        });
    }
  }

  stop(): void {
    clearTimeout(this.idleTimer);
    this.interruptSub?.unsubscribe();
    this.storageSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
  }

  private broadcastActivity(): void {
    try {
      localStorage.setItem(this.storageKey, Date.now().toString());
    } catch {
      console.log('[IdleService] broadcastActivity failed, storage unavailable');
    }
  }

  private startIdlePhase(): void {
    clearTimeout(this.idleTimer);
    this.countdownSub?.unsubscribe();
    this.idleTimer = setTimeout(() => this.startTimeoutCountdown(), this.idleSeconds * 1000);
  }

  private startTimeoutCountdown(): void {
    let remaining = this.timeoutSeconds;
    this.countdownSub = timer(0, 1000).subscribe(() => {
      this.onTimeoutWarning.next(remaining);
      if (remaining <= 0) {
        this.countdownSub?.unsubscribe();
        this.onTimeout.next();
        return;
      }
      remaining--;
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
